import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse a date-only string (YYYY-MM-DD) as a local date, not UTC.
 * Fixes timezone offset bug where "2024-12-09" parsed as UTC midnight
 * would become Dec 8 when displayed in US timezones.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object at noon local time, or null if input is null/empty
 */
export function parseLocalDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  // Append T12:00:00 to treat as noon local time, avoiding any timezone boundary issues
  // This ensures the date stays correct regardless of user's timezone
  return new Date(dateString + 'T12:00:00');
}

/**
 * Format a number as US currency.
 *
 * @param value - The number to format
 * @param options - Intl.NumberFormatOptions overrides
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number | null | undefined,
  options?: Intl.NumberFormatOptions
): string {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  }).format(value);
}

/**
 * Safely download a Blob as a file.
 * Handles DOM cleanup gracefully to avoid DOMException errors when the node
 * has been removed before cleanup (e.g., user navigated away).
 *
 * @param blob - The Blob to download
 * @param filename - The filename for the download
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Delayed cleanup with safe removal
  setTimeout(() => {
    try {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    } catch {
      // Node already removed, ignore
    }
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Safely download content from a URL as a file.
 * Handles DOM cleanup gracefully to avoid DOMException errors.
 *
 * @param url - The URL to download from
 * @param filename - The filename for the download
 */
export function downloadUrl(url: string, filename: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Delayed cleanup with safe removal
  setTimeout(() => {
    try {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    } catch {
      // Node already removed, ignore
    }
  }, 100);
}
